import { Suspense, lazy, useEffect, useState } from 'react';
import { useUserSessionContext } from '../../../components/UserSessionContextWrapper';
import useMessageService from '../../../services/i18n/useMessageService';
import { userGeneralInfo } from '../myAccountApi';
import MyAccountLayout from '../../../components/MyAccountLayout';
import NavigationTabs from '../../../constants/NavigationTabs';
import ResponseStatus from '../../../constants/ResponseStatus';
import useNetworkErrorNotification from '../../../services/useNetworkErrorNotification';
import useLanguagesLocalesTimezones from '../../../services/useLanguagesLocalesTimezones';
import GeneralEdit from './GeneralEdit';

const GeneralView = lazy(() => import(/* webpackChunkName: "GeneralView" */ './GeneralView'));

const General = () => {
    const [viewMode, setViewMode] = useState(true);
    const { showNotification: showNetworkErrorNotification } = useNetworkErrorNotification();
    const { handleResponseSessionErrors } = useUserSessionContext();
    const [generalInfo, setGeneralInfo] = useState({});
    const [options, { localesResponse }, setLocaleOptions] = useLanguagesLocalesTimezones(
        generalInfo.language,
    );
    const [
        [
            saveLabel = 'Save',
            editLabel = 'Edit',
            cancelLabel = 'Cancel',
            firstNameLabel = 'First Name',
            lastNameLabel = 'Last Name',
            countryCodeLabel = 'Country Code',
            mobilePhoneLabel = 'Mobile Phone',
            languageLabel = 'Language',
            localeLabel = 'Locale',
            timezoneLabel = 'Timezone',
            phoneNumberErrorLabel = 'Phone number is required when country code is selected',
        ],
    ] = useMessageService([
        'general.save',
        'general.edit',
        'general.cancel',
        'general.first_name',
        'general.last_name',
        'general.country_code',
        'general.mobile_phone',
        'general.language',
        'general.locale',
        'general.timezone',
        'general.validation_phone_number_required_with_country_code',
    ]);

    const showEditMode = () => {
        setViewMode(false);
    };

    const showViewMode = () => {
        setViewMode(true);
    };

    const handleSave = (updatedGeneralInfo) => {
        setGeneralInfo(updatedGeneralInfo);
        setViewMode(true);
    };

    useEffect(() => {
        (async () => {
            const response = await userGeneralInfo();
            if (handleResponseSessionErrors(response)) {
                return;
            }

            const { responseStatus, data } = response;

            switch (responseStatus) {
                case ResponseStatus.SUCCESS:
                    setGeneralInfo(data);
                    break;
                case ResponseStatus.UI_NETWORK_ERROR:
                    await showNetworkErrorNotification();
                    break;
                default:
                // No-op
            }
        })();

        // showNetworkErrorNotification is updated when users save a new language
        // - prevent fetching for user info because we've already captured it in state
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [handleResponseSessionErrors]);

    const fieldLabels = {
        saveLabel,
        editLabel,
        cancelLabel,
        firstNameLabel,
        lastNameLabel,
        countryCodeLabel,
        mobilePhoneLabel,
        languageLabel,
        localeLabel,
        timezoneLabel,
        phoneNumberErrorLabel,
    };

    return (
        <Suspense fallback="">
            <MyAccountLayout selectedTab={NavigationTabs.GENERAL}>
                {viewMode ? (
                    <GeneralView
                        generalInfo={generalInfo}
                        fieldLabels={fieldLabels}
                        options={options}
                        showEditMode={showEditMode}
                    />
                ) : (
                    <GeneralEdit
                        generalInfo={generalInfo}
                        fieldLabels={fieldLabels}
                        options={options}
                        onCancel={showViewMode}
                        onSave={handleSave}
                        setLocaleOptions={setLocaleOptions}
                        shouldDecoupleLanguageLocale={
                            localesResponse.data.isLanguageLocaleDecoupled
                        }
                    />
                )}
            </MyAccountLayout>
        </Suspense>
    );
};

export default General;
